import React, { useState } from 'react';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import { ProConfigProvider } from '@ant-design/pro-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import logo from '../../../assets/image/logoSeeWard.png';
import { seewardColors, seewardTheme } from '../../../theme/seewardTheme';
import UserMenu from '../UserMenu';
import ContextSwitcher, { 
  useSwitcherHotkeys 
} from '../context/ContextSwitcher';
import LevelTag from '../context/LevelTag';
import ResourceIcon, { kindIcon, kindName } from './ResourceIcon';
import './resource.css';

export type ResourceKind = keyof typeof kindName;

interface ResourceShellProps {
  /** Output of `buildMenu`, or the platform entries: same shape. */
  menu: any[];
  /** Platform variant: "Administration" badge, no resource button. */
  platform?: boolean;
  /** Sidebar heading. */
  sidebarLabel?: string;
  /** What the resource button shows (non-platform only). */
  resource?: { name: string; kind: ResourceKind };
  /** Extra topbar content before the account menu, e.g. the Sandbox button. */
  topbarExtras?: React.ReactNode;
  userMenu?: React.ComponentProps<typeof UserMenu>;
  homePath?: string;
  contentClassName?: string;
}

export default function ResourceShell({
  menu,
  platform = false,
  sidebarLabel,
  resource,
  topbarExtras,
  userMenu,
  homePath = '/',
  contentClassName
}: ResourceShellProps) {
  const location = useLocation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  useSwitcherHotkeys(setPickerOpen);

  const isActive = (path?: string) => {
    if (!path) return false;
    const pathname = path.split('?')[0];
    if (pathname === '/' || pathname === '/admin') return location.pathname === pathname;
    return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
  };

  /* Console pages (org, entity) and platform pages pad themselves; workspace pages
     rely on the shell, as they did under the old layout's `application-content`. */
  const isConsolePage = /^\/(org|entity)\//.test(location.pathname);
  const defaultContentClass = platform
    ? 'sw-platform-page'
    : isConsolePage
      ? 'sw-admin-content'
      : location.pathname === '/remediation/applications'
        ? 'sw-admin-content sw-workspace-content application-content application-content-list'
        : 'sw-admin-content sw-workspace-content application-content';

  return (
    <ConfigProvider
      locale={enUS}
      theme={seewardTheme}
      getTargetContainer={() => document.getElementById('pro-layout') || document.body}
    >
      <ProConfigProvider hashed={false}>
        <div className="sw-app" id="pro-layout">
          <header className="sw-topbar">
            <Link className="sw-brand" to={homePath} aria-label="SeeWard home">
              <img
                src={logo}
                alt="SeeWard"
                style={{ width: 145, height: 32, objectFit: 'contain' }}
              />
            </Link>
            {platform && (
              <span className="sw-mode-badge administration">
                <ResourceIcon name="shield" size={15} />
                Administration
              </span>
            )}
            {!platform && resource && (
              <button
                className="sw-resource-button"
                aria-label="Select organisation, entity or workspace"
                title="Select organisation, entity or workspace"
                aria-haspopup="dialog"
                onClick={() => setPickerOpen(true)}
              >
                <ResourceIcon name={kindIcon[resource.kind]} />
                <span className="sw-truncate sw-grow">{resource.name}</span>
                <LevelTag
                  label={kindName[resource.kind]}
                  level={resource.kind}
                />
                <ResourceIcon name="down" size={15} />
              </button>
            )}
            <div className="sw-grow" />
            {topbarExtras}
            <UserMenu {...userMenu} />
          </header>
          <div className="sw-body">
            <aside className="sw-sider" aria-label="Main navigation">
              {sidebarLabel && <div 
                className="sw-sider-label"
              >{sidebarLabel}</div>}
              <nav>
                {menu.map((item, i) => {
                  if (item.type === 'divider') {
                    return (
                      <div
                        key={`divider-${i}`}
                        role="separator"
                        style={{ 
                          height: 1, 
                          margin: '8px 12px', 
                          background: seewardColors.border 
                        }}
                      />
                    );
                  }
                  const children = item.routes as any[] | undefined;
                  const hasChildren = Boolean(children?.length);
                  const childActive = children?.some((child) => isActive(child.path));
                  const isExpanded = expanded[String(item.key)] ?? childActive;
                  return (
                    <React.Fragment key={item.key ?? i}>
                      <div
                        className={`sw-nav ${isActive(item.path) || childActive ? 'active' : ''}`}
                        style={{ padding: 0 }}
                      >
                        <Link className="sw-nav sw-grow" to={item.path ?? '#'}>
                          {item.icon}
                          {item.name}
                          {item.alert && (
                            <span
                              aria-label="Needs attention"
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#ff4d4f',
                                marginLeft: 6
                              }}
                            />
                          )}
                        </Link>
                        {hasChildren && (
                          <button
                            className="sw-chev"
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${String(item.key)}`}
                            aria-expanded={!!isExpanded}
                            onClick={() =>
                              setExpanded((old) => ({ 
                                ...old, 
                                [String(item.key)]: !isExpanded 
                              }))
                            }
                          >
                            <ResourceIcon 
                              name={isExpanded ? 'down' : 'right'} 
                              size={15} /
                            >
                          </button>
                        )}
                      </div>
                      {hasChildren &&
                        isExpanded &&
                        children!.map((child) => (
                          <Link
                            key={child.key}
                            className={`sw-nav ${isActive(child.path) ? 'active' : ''}`}
                            style={{ paddingLeft: 41 }}
                            to={child.path}
                          >
                            {child.name}
                          </Link>
                        ))}
                    </React.Fragment>
                  );
                })}
              </nav>
            </aside>
            <main className="sw-content" id="page-scroll">
              <div 
                className={contentClassName ?? defaultContentClass}
              >
                <Outlet />
              </div>
            </main>
          </div>
          {!platform && (
            <ContextSwitcher 
              open={pickerOpen} 
              onClose={() => setPickerOpen(false)} /
            >
          )}
        </div>
      </ProConfigProvider>
    </ConfigProvider>
  );
}
